// © Microsoft Corporation. All rights reserved.

import { CookieConsentInfo, CookieConsentStatus as CookieConsentStatusInfo, OneTrustCookieCategory } from "@msnews/cookie-contracts";

import { ActionMap } from "@msnews/experiences-redux";
import { Location } from "./ExperiencesSharedState.state";
import { SignedInStatus as SignedInStatusInfo } from "@msnews/experiences-constants";

type SignedInStatus = (signedIn: SignedInStatusInfo) => boolean;
type SignedInStatusConfirmed = () => void;
type CookieConsentStatus = (consentStatus: CookieConsentStatusInfo) => void;
type SetCookieConsentInfo = (cookieConsentInfo: CookieConsentInfo) => void;
type OneTrustActiveCookieCategories = (activeCategories: Array<OneTrustCookieCategory>) => void;
type UseEnterpriseCompliance = (useEnterpriseCompliance: boolean) => void;
type UseEnterpriseDomain = (useEnterpriseDomain: boolean) => void;
type UseHistory = (useHistory: boolean) => void;
type UpdateLocation = (location: Location) => void;

/**
 * Class containing actions that apply to the ExperiencesSharedState connector
 * @class
 */
export class ExperiencesSharedStateActions {
    /**
     * Action for signed-in status state update. Be careful with where this is fired as it's used to refresh the page when status changes in some canvases (Windows HP).
     */
    public static signedInStatus: ActionMap<SignedInStatus> = new ActionMap<SignedInStatus>("SignedInStatus");

    /** Action that's fired when we've confirmed the initial sign in state with the user's actual status.
     * This is used to confirm that the user's sign in status has successfully been fixed after a refresh.
     */
    public static signedInStatusConfirmed: ActionMap<SignedInStatusConfirmed> = new ActionMap<SignedInStatusConfirmed>("SignedInStatusConfirmed");

    /**
     * Action for setting cookie consent info
     */
    public static setCookieConsentInfo: ActionMap<SetCookieConsentInfo> = new ActionMap<SetCookieConsentInfo>("SetCookieConsentInfo");

    /**
     * Action for cookie consent status state update
     */
    public static cookieConsentStatus: ActionMap<CookieConsentStatus> = new ActionMap<CookieConsentStatus>("CookieConsentStatus");

    /**
     * Action for useEnterpriseCompliance state update
     */
    public static oneTrustActiveCookieCategories: ActionMap<OneTrustActiveCookieCategories> = new ActionMap<OneTrustActiveCookieCategories>("OneTrustActiveCookieCategories");

    /**
     * Action for useEnterpriseCompliance state update
     */
    public static useEnterpriseCompliance: ActionMap<UseEnterpriseCompliance> = new ActionMap<UseEnterpriseCompliance>("UseEnterpriseCompliance");

    /**
     * Action for useEnterpriseDomain state update
     */
    public static useEnterpriseDomain: ActionMap<UseEnterpriseDomain> = new ActionMap<UseEnterpriseDomain>("UseEnterpriseDomain");

    /**
     * Action for useHistory state update
     */
    public static useHistory: ActionMap<UseHistory> = new ActionMap<UseHistory>("UseHistory");

    /**
     * Action for update location
     */
    public static updateLocation: ActionMap<UpdateLocation> = new ActionMap<UpdateLocation>("UpdateLocation");
}
